import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  Loader2,
  TrendingUp,
  TrendingDown,
  Search,
  ArrowLeft,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { useSearch } from "../context/SearchContext";
import api from "../lib/api";
import { toast } from "sonner";

const RANGES = ["1M", "3M", "1Y"];
const COLOR_A = "#547A8F";
const COLOR_B = "#D18E63";

// Frozen at module scope so they don't allocate a new object on every render
// (Recharts uses ref equality on these props to skip re-renders).
const CHART_MARGIN = { top: 5, right: 12, left: 0, bottom: 0 };
const X_TICK_STYLE = { fontSize: 11, fill: "#6B7068" };
const Y_TICK_STYLE = { fontSize: 11, fill: "#6B7068" };
const AXIS_LINE_STYLE = { stroke: "#EAE6E1" };
const TOOLTIP_CONTENT_STYLE = { backgroundColor: "#FFFFFF", border: "1px solid #EAE6E1", borderRadius: 8, fontSize: 12 };
const TOOLTIP_LABEL_STYLE = { color: "#6B7068", fontSize: 11 };

const formatNum = (n) => {
  if (n == null) return "—";
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(2);
};

function StatRow({ label, valA, valB, color = "#2A2F2B" }) {
  return (
    <div className="grid grid-cols-3 items-center px-4 py-2.5 border-b border-[#EAE6E1] last:border-0">
      <div className="text-xs text-[#6B7068] uppercase tracking-[0.15em] font-semibold">
        {label}
      </div>
      <div className="text-right font-mono-ticker text-sm" style={{ color }}>
        {valA}
      </div>
      <div className="text-right font-mono-ticker text-sm" style={{ color }}>
        {valB}
      </div>
    </div>
  );
}

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { openSearch } = useSearch();
  const [range, setRange] = useState("1Y");
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(false);

  const aType = params.get("a_type") || "stock";
  const aSymbol = params.get("a") || "";
  const bType = params.get("b_type") || "stock";
  const bSymbol = params.get("b") || "";

  useEffect(() => {
    if (!aSymbol || !bSymbol) {
      setA(null);
      setB(null);
      return;
    }
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get(`/quote/${aType}/${aSymbol}?range=${range}`).catch(() => null),
      api.get(`/quote/${bType}/${bSymbol}?range=${range}`).catch(() => null),
    ])
      .then(([ra, rb]) => {
        if (cancel) return;
        setA(ra?.data || null);
        setB(rb?.data || null);
        if (!ra?.data || !rb?.data) {
          toast.error("Could not load one of the assets");
        }
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [aType, aSymbol, bType, bSymbol, range]);

  // Build a normalized series: each asset's price as % of its starting price
  const merged = React.useMemo(() => {
    if (!a?.points || !b?.points) return [];
    const startA = a.points[0]?.c;
    const startB = b.points[0]?.c;
    if (!startA || !startB) return [];
    // Normalize each independently then join by index — use whichever is shorter
    const len = Math.min(a.points.length, b.points.length);
    const out = [];
    for (let i = 0; i < len; i++) {
      const pA = a.points[i];
      const pB = b.points[i];
      out.push({
        t: pA.t,
        a: ((pA.c - startA) / startA) * 100,
        b: ((pB.c - startB) / startB) * 100,
      });
    }
    return out;
  }, [a, b]);

  const fmtX = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch (_) {
      return iso;
    }
  };

  const swapAssets = () => {
    const sp = new URLSearchParams(params);
    sp.set("a", bSymbol);
    sp.set("a_type", bType);
    sp.set("b", aSymbol);
    sp.set("b_type", aType);
    setParams(sp);
  };

  const change = (q) => (q?.change_pct != null ? q.change_pct : null);
  const colorA = (change(a) ?? 0) >= 0 ? "#5C8D73" : "#C66B59";
  const colorB = (change(b) ?? 0) >= 0 ? "#5C8D73" : "#C66B59";

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8 md:py-12" data-testid="compare-page">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B7068] hover:text-[#2A2F2B] mb-6 transition-soft"
          data-testid="compare-back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Dashboard
        </Link>

        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
                Compare
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B]">
              {aSymbol && bSymbol ? `${aSymbol} vs ${bSymbol}` : "Compare two assets"}
            </h1>
            <p className="text-[#6B7068] mt-2 text-sm leading-relaxed max-w-xl">
              Lines normalized to 0% on day one — see relative performance at a glance.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={openSearch}
              variant="outline"
              data-testid="compare-pick-asset-btn"
              className="rounded-full bg-white border-[#EAE6E1] hover:border-[#547A8F] text-[#2A2F2B]"
            >
              <Search className="w-4 h-4 mr-2" strokeWidth={1.5} /> Pick assets
            </Button>
            {aSymbol && bSymbol && (
              <Button
                onClick={swapAssets}
                variant="outline"
                data-testid="compare-swap-btn"
                className="rounded-full bg-white border-[#EAE6E1] hover:border-[#547A8F] text-[#2A2F2B]"
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" strokeWidth={1.5} /> Swap
              </Button>
            )}
          </div>
        </header>

        {/* Asset picker chips */}
        {(!aSymbol || !bSymbol) && (
          <div
            className="rounded-2xl bg-white border border-[#EAE6E1] p-8 text-center"
            data-testid="compare-empty"
          >
            <ArrowLeftRight className="w-10 h-10 text-[#D18E63] mx-auto mb-3" strokeWidth={1.25} />
            <h3 className="font-heading text-xl text-[#2A2F2B] mb-1">
              Pick two assets to compare
            </h3>
            <p className="text-sm text-[#6B7068] max-w-md mx-auto mb-4">
              Type both symbols separated by a space (e.g. <code className="font-mono-ticker bg-[#F9F8F6] px-1.5 py-0.5 rounded">AAPL MSFT</code>),
              or open search and pick.
            </p>
            <CompareInline onPick={(p) => navigate(`/compare?a=${p.aSym}&a_type=${p.aType}&b=${p.bSym}&b_type=${p.bType}`)} />
          </div>
        )}

        {aSymbol && bSymbol && (
          <>
            <div className="flex items-center gap-1 p-1 rounded-full bg-white border border-[#EAE6E1] w-fit mb-6" data-testid="compare-range-tabs">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  data-testid={`compare-range-${r}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-mono-ticker font-semibold transition-soft ${
                    range === r ? "bg-[#2A2F2B] text-white" : "text-[#6B7068] hover:text-[#2A2F2B]"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

            {/* Two header tiles */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                { q: a, sym: aSymbol, color: COLOR_A, trend: colorA, side: "a" },
                { q: b, sym: bSymbol, color: COLOR_B, trend: colorB, side: "b" },
              ].map((row) => (
                <div
                  key={row.side}
                  data-testid={`compare-header-${row.side}`}
                  className="rounded-2xl bg-white border border-[#EAE6E1] p-5"
                  style={{ borderTopColor: row.color, borderTopWidth: 4 }}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div>
                      <div className="font-mono-ticker text-xs text-[#6B7068]">
                        {row.sym}
                      </div>
                      <div className="font-heading text-2xl tracking-tight text-[#2A2F2B]">
                        {row.q?.name || "—"}
                      </div>
                    </div>
                    {row.q?.price != null && (
                      <div className="text-right">
                        <div className="font-mono-ticker text-2xl text-[#2A2F2B]">
                          ${row.q.price < 1 ? row.q.price.toFixed(4) : row.q.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="font-mono-ticker text-xs font-semibold flex items-center justify-end gap-1" style={{ color: row.trend }}>
                          {(row.q.change_pct ?? 0) >= 0 ? <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> : <TrendingDown className="w-3 h-3" strokeWidth={2.5} />}
                          {row.q.change_pct?.toFixed(2)}% {range}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Overlay chart */}
            <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5 mb-6" data-testid="compare-chart">
              {loading || merged.length === 0 ? (
                <div className="h-[360px] flex items-center justify-center text-sm text-[#6B7068]">
                  {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...</> : "Not enough data"}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={380}>
                  <LineChart data={merged} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE6E1" />
                    <XAxis
                      dataKey="t"
                      tickFormatter={fmtX}
                      tick={X_TICK_STYLE}
                      axisLine={AXIS_LINE_STYLE}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v.toFixed(0)}%`}
                      tick={Y_TICK_STYLE}
                      axisLine={AXIS_LINE_STYLE}
                      width={50}
                    />
                    <Tooltip
                      formatter={(v, n) => [`${v.toFixed(2)}%`, n === "a" ? aSymbol : bSymbol]}
                      labelFormatter={fmtX}
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                    />
                    <Legend
                      formatter={(value) => (value === "a" ? aSymbol : bSymbol)}
                      iconType="line"
                      wrapperStyle={{ fontSize: 12 }}
                    />
                    <Line type="monotone" dataKey="a" stroke={COLOR_A} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="b" stroke={COLOR_B} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Stat comparison */}
            <div className="rounded-2xl bg-white border border-[#EAE6E1] overflow-hidden" data-testid="compare-stats">
              <div className="grid grid-cols-3 px-4 py-3 border-b border-[#EAE6E1] bg-[#F9F8F6]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
                  Metric
                </div>
                <div className="text-right text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: COLOR_A }}>
                  {aSymbol}
                </div>
                <div className="text-right text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: COLOR_B }}>
                  {bSymbol}
                </div>
              </div>
              <StatRow
                label="Price"
                valA={a?.price != null ? `$${formatNum(a.price)}` : "—"}
                valB={b?.price != null ? `$${formatNum(b.price)}` : "—"}
              />
              <StatRow
                label={`${range} change`}
                valA={a?.change_pct != null ? `${a.change_pct >= 0 ? "+" : ""}${a.change_pct.toFixed(2)}%` : "—"}
                valB={b?.change_pct != null ? `${b.change_pct >= 0 ? "+" : ""}${b.change_pct.toFixed(2)}%` : "—"}
              />
              <StatRow
                label="High"
                valA={a?.high != null ? `$${formatNum(a.high)}` : "—"}
                valB={b?.high != null ? `$${formatNum(b.high)}` : "—"}
              />
              <StatRow
                label="Low"
                valA={a?.low != null ? `$${formatNum(a.low)}` : "—"}
                valB={b?.low != null ? `$${formatNum(b.low)}` : "—"}
              />
              <StatRow
                label="Market Cap"
                valA={a?.info?.market_cap ? `$${formatNum(a.info.market_cap)}` : "—"}
                valB={b?.info?.market_cap ? `$${formatNum(b.info.market_cap)}` : "—"}
              />
              <StatRow
                label="52w High"
                valA={a?.info?.year_high ? `$${formatNum(a.info.year_high)}` : "—"}
                valB={b?.info?.year_high ? `$${formatNum(b.info.year_high)}` : "—"}
              />
              <StatRow
                label="52w Low"
                valA={a?.info?.year_low ? `$${formatNum(a.info.year_low)}` : "—"}
                valB={b?.info?.year_low ? `$${formatNum(b.info.year_low)}` : "—"}
              />
              <StatRow
                label="Volume (range)"
                valA={a?.volume != null ? formatNum(a.volume) : "—"}
                valB={b?.volume != null ? formatNum(b.volume) : "—"}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// Tiny inline picker — quick way to enter two tickers without leaving the page
function CompareInline({ onPick }) {
  const [v, setV] = useState("");
  const submit = () => {
    const parts = v.toUpperCase().split(/\s+/).filter(Boolean);
    if (parts.length < 2) {
      toast.error("Enter two tickers, e.g. AAPL MSFT");
      return;
    }
    onPick({ aSym: parts[0], aType: "stock", bSym: parts[1], bType: "stock" });
  };
  return (
    <div className="max-w-sm mx-auto flex items-center gap-2">
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="AAPL MSFT"
        data-testid="compare-inline-input"
        className="flex-1 rounded-full border border-[#EAE6E1] bg-white px-4 py-2 text-sm font-mono-ticker focus:outline-none focus:border-[#547A8F]"
        onKeyDown={(e) => e.key === "Enter" && submit()}
      />
      <Button
        onClick={submit}
        data-testid="compare-inline-submit"
        className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
      >
        Compare
      </Button>
    </div>
  );
}
